import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Spinner, Badge, Modal, Table, Form, Pagination } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faClock, faUserEdit, faPlus, faEdit, faTrash, faSignInAlt, faSignOutAlt, faEye, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ApiManager } from '../../../core/api-manager';
import { DataManager } from '../../../core/data-manager';
import { toast } from 'react-toastify';

const ActivityTab = ({ timeRange, customDateRange }) => {
    const [loading, setLoading] = useState(true);
    const [activityLog, setActivityLog] = useState([]);
    const [showItemModal, setShowItemModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [loadingItem, setLoadingItem] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const recordsPerPage = 50;

    useEffect(() => {
        loadActivityData();
    }, [timeRange, customDateRange, currentPage]);
    
    useEffect(() => {
        // Reset alla prima pagina quando cambia la ricerca
        setCurrentPage(1);
        const delayDebounceFn = setTimeout(() => {
            loadActivityData();
        }, 500); // Debounce di 500ms per la ricerca
        
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    const loadActivityData = async () => {
        setLoading(true);
        try {
            // Preparazione parametri per l'API
            const apiParams = { 
                timeRange,
                limit: recordsPerPage,
                page: currentPage,
                search: searchTerm
            };
            if (timeRange === 'custom' && customDateRange.start && customDateRange.end) {
                apiParams.startDate = customDateRange.start;
                apiParams.endDate = customDateRange.end;
            }

            const response = await ApiManager.sendAuthenticatedRequest('/analytics/activity', apiParams);

            const responseData = response?.data || response || {};
            setActivityLog(responseData.records || []);
            setTotalRecords(responseData.total || 0);
            setTotalPages(Math.ceil((responseData.total || 0) / recordsPerPage));
        } catch (error) {
            console.error('Error loading activity data:', error);
            toast.error('Errore nel caricamento log attività');
        } finally {
            setLoading(false);
        }
    };

    const handleViewItem = async (entity, itemId) => {
        setLoadingItem(true);
        setShowItemModal(true);
        try {
            // Usa DataManager.getItem per recuperare l'elemento
            const response = await DataManager.getItem(entity, itemId);
            
            // Estrai solo il body dalla risposta
            const itemData = response?.body || response;
            
            if (itemData) {
                setSelectedItem({
                    entity,
                    id: itemId,
                    data: itemData
                });
            } else {
                throw new Error('Elemento non trovato');
            }
        } catch (error) {
            console.error('Error loading item:', error);
            toast.error('Errore nel caricamento dell\'elemento');
            setShowItemModal(false);
        } finally {
            setLoadingItem(false);
        }
    };

    const getOperationIcon = (operation) => {
        switch(operation) {
            case '2_ADD': return faPlus;
            case '1_EDIT': return faEdit;
            case '0_DELETE': return faTrash;
            case '3_LOGIN': return faSignInAlt;
            case '4_LOGOUT': return faSignOutAlt;
            default: return faClock;
        }
    };

    const getOperationLabel = (operation) => {
        switch(operation) {
            case '2_ADD': return 'Creazione';
            case '1_EDIT': return 'Modifica';
            case '0_DELETE': return 'Eliminazione';
            case '3_LOGIN': return 'Login';
            case '4_LOGOUT': return 'Logout';
            default: return operation;
        }
    };

    const getOperationColor = (operation) => {
        switch(operation) {
            case '2_ADD': return 'success';
            case '1_EDIT': return 'warning';
            case '0_DELETE': return 'danger';
            case '3_LOGIN': return 'info';
            case '4_LOGOUT': return 'secondary';
            default: return 'dark';
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center py-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Caricamento...</span>
                </Spinner>
            </div>
        );
    }

    return (
        <>
            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <Card.Title>Log Attività</Card.Title>
                                <Form.Control
                                    type="text"
                                    placeholder="Cerca utente, entità o ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    size="sm"
                                    style={{ width: '250px' }}
                                />
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {activityLog && activityLog.length > 0 ? (
                                <>
                                    <Table responsive hover size="sm" className="small">
                                        <thead>
                                            <tr>
                                                <th>Data/Ora</th>
                                                <th>Utente</th>
                                                <th>Operazione</th>
                                                <th>Entità</th>
                                                <th>ID Elemento</th>
                                                <th>Dettagli</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {activityLog.map((activity, idx) => (
                                            <tr key={idx}>
                                                <td className="text-nowrap">
                                                    {activity.date ? new Date(activity.date).toLocaleString('it-IT') : 'N/A'}
                                                </td>
                                                <td>
                                                    <strong>{activity.username || 'Sistema'}</strong>
                                                    {activity.email && (
                                                        <div className="small text-muted">{activity.email}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <Badge bg={getOperationColor(activity.operation)}>
                                                        <FontAwesomeIcon icon={getOperationIcon(activity.operation)} className="me-1" />
                                                        {getOperationLabel(activity.operation)}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    {activity.entity || '-'}
                                                </td>
                                                <td>
                                                    {activity.idItem && activity.entity && activity.operation !== '3_LOGIN' && activity.operation !== '4_LOGOUT' ? (
                                                        <Button
                                                            variant="link"
                                                            size="sm"
                                                            className="p-0"
                                                            onClick={() => handleViewItem(activity.entity, activity.idItem)}
                                                        >
                                                            <FontAwesomeIcon icon={faEye} className="me-1" />
                                                            {activity.idItem}
                                                        </Button>
                                                    ) : (
                                                        activity.idItem || '-'
                                                    )}
                                                </td>
                                                <td>
                                                    {activity.details || activity.message || '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                                
                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="text-muted small">
                                            Mostrando {((currentPage - 1) * recordsPerPage) + 1} - {Math.min(currentPage * recordsPerPage, totalRecords)} di {totalRecords} risultati
                                        </div>
                                        <Pagination size="sm">
                                            <Pagination.First 
                                                onClick={() => setCurrentPage(1)} 
                                                disabled={currentPage === 1}
                                            />
                                            <Pagination.Prev 
                                                onClick={() => setCurrentPage(currentPage - 1)} 
                                                disabled={currentPage === 1}
                                            />
                                            
                                            {/* Mostra max 5 pagine */}
                                            {[...Array(Math.min(5, totalPages))].map((_, index) => {
                                                let pageNum = 1;
                                                if (currentPage <= 3) {
                                                    pageNum = index + 1;
                                                } else if (currentPage >= totalPages - 2) {
                                                    pageNum = totalPages - 4 + index;
                                                } else {
                                                    pageNum = currentPage - 2 + index;
                                                }
                                                
                                                if (pageNum > 0 && pageNum <= totalPages) {
                                                    return (
                                                        <Pagination.Item 
                                                            key={pageNum}
                                                            active={pageNum === currentPage}
                                                            onClick={() => setCurrentPage(pageNum)}
                                                        >
                                                            {pageNum}
                                                        </Pagination.Item>
                                                    );
                                                }
                                                return null;
                                            })}
                                            
                                            <Pagination.Next 
                                                onClick={() => setCurrentPage(currentPage + 1)} 
                                                disabled={currentPage === totalPages}
                                            />
                                            <Pagination.Last 
                                                onClick={() => setCurrentPage(totalPages)} 
                                                disabled={currentPage === totalPages}
                                            />
                                        </Pagination>
                                    </div>
                                )}
                                </>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    <FontAwesomeIcon icon={faClock} size="3x" className="mb-3" />
                                    <p>Nessuna attività nel periodo selezionato</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal per visualizzazione elemento */}
            <Modal 
                show={showItemModal} 
                onHide={() => setShowItemModal(false)} 
                size="xl"
                scrollable={true}
                dialogClassName="modal-90w"
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6">
                        Dettagli {selectedItem?.entity} #{selectedItem?.id}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {loadingItem ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" />
                        </div>
                    ) : selectedItem?.data ? (
                        <pre className="p-3 rounded mb-0" style={{ fontSize: '0.7rem' }}>
                            {JSON.stringify(selectedItem.data, null, 2)}
                        </pre>
                    ) : (
                        <p>Nessun dato disponibile</p>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
};

export default ActivityTab;